import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { CATEGORY_LABELS, type CategorySlug } from '../lib/types'

interface BoothData {
  id: string
  name: string
  booth: string
  desc: string
  cat: string
  industry: string
  founded: number | null
  employees: string | null
  hq: string | null
  revenue: string | null
}

interface BoothPos {
  x: number
  y: number
  h: string
  p: string
  n: string
}

const CAT_COLORS: Record<string, string> = {
  'robotics': '#ef4444',
  'ai-ml': '#8b5cf6',
  'machine-vision': '#3b82f6',
  'motion-control': '#f59e0b',
  'industrial-automation': '#10b981',
  'software-digital-twin': '#6366f1',
  'safety-compliance': '#f97316',
  'b2b-services': '#64748b',
  'sensors-iot': '#06b6d4',
  'logistics-warehousing': '#84cc16',
  'humanoid-robots': '#ec4899',
  'components-connectors': '#78716c',
  'education-training': '#a855f7',
  'system-integrators': '#14b8a6',
  'pneumatics-hydraulics': '#ea580c',
  'metrology-inspection': '#2563eb',
  'embedded-computing': '#7c3aed',
  'mechanical-components': '#737373',
  'amr-agv': '#dc2626',
  'grippers-eol': '#059669',
  'welding-joining': '#d97706',
  '3d-printing': '#0891b2',
  'startups': '#f43f5e',
  'automotive-ev': '#0d9488',
  'precision-machining': '#b45309',
  'medical-life-sciences': '#db2777',
  'energy-power': '#ca8a04',
  'material-handling': '#65a30d',
  'testing-qc': '#4f46e5',
  'laser-optics': '#e11d48',
  'other': '#94a3b8',
}

function parsePoints(startX: number, startY: number, pointsStr: string, scale: number): string {
  const pairs = pointsStr.split('|').map(p => {
    const [dx, dy] = p.split(',').map(Number)
    return [(startX + dx) * scale, (startY + dy) * scale]
  })
  return pairs.map(([x, y]) => `${x},${y}`).join(' ')
}

export default function FloorView() {
  const [booths, setBooths] = useState<BoothData[]>([])
  const [positions, setPositions] = useState<Record<string, BoothPos>>({})
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<BoothData | null>(null)
  const [pinned, setPinned] = useState<BoothData | null>(null)
  const [highlightCat, setHighlightCat] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [activeHall, setActiveHall] = useState<'A' | 'B'>('A')
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: 1400, h: 1300 })
  const [isMobile, setIsMobile] = useState(false)
  const svgRef = useRef<SVGSVGElement>(null)
  const isPanning = useRef(false)
  const panStart = useRef({ x: 0, y: 0, vx: 0, vy: 0 })
  const searchInputRef = useRef<HTMLInputElement>(null)
  const lastTouchDist = useRef<number | null>(null)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    Promise.all([
      fetch('/api/floor-view').then(r => r.json()),
      fetch('/booth-positions.json').then(r => r.json()),
    ]).then(([b, p]) => {
      setBooths(b)
      setPositions(p)
    }).finally(() => setLoading(false))
  }, [])

  const scale = 0.1

  const hallBounds = useMemo(() => ({
    A: { minX: 1317 * scale, maxX: 13063 * scale, minY: 2324 * scale, maxY: 12356 * scale },
    B: { minX: 225 * scale, maxX: 9785 * scale, minY: 664 * scale, maxY: 5560 * scale },
  }), [scale])

  useEffect(() => {
    const b = hallBounds[activeHall]
    const pad = 20
    setViewBox({
      x: b.minX - pad,
      y: b.minY - pad,
      w: (b.maxX - b.minX) + pad * 2,
      h: (b.maxY - b.minY) + pad * 2,
    })
  }, [activeHall, hallBounds])

  const hallBooths = useMemo(() => {
    return booths.filter(b => {
      const pos = positions[b.booth]
      return pos && pos.h === activeHall
    })
  }, [booths, positions, activeHall])

  const neighbors = useMemo(() => {
    if (!selected || !positions[selected.booth]) return []
    const sp = positions[selected.booth]
    return hallBooths
      .filter(b => b.id !== selected.id && positions[b.booth])
      .map(b => {
        const bp = positions[b.booth]
        const dist = Math.sqrt((bp.x - sp.x) ** 2 + (bp.y - sp.y) ** 2)
        return { ...b, dist }
      })
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 30)
  }, [selected, hallBooths, positions])

  const searchResults = useMemo(() => {
    if (!search || search.length < 2) return null
    const q = search.toLowerCase()
    return booths.filter(b =>
      b.booth === search ||
      b.name.toLowerCase().includes(q) ||
      b.industry.toLowerCase().includes(q)
    ).slice(0, 20)
  }, [search, booths])

  const handleBoothClick = useCallback((b: BoothData) => {
    setSelected(b)
    setPinned(b)
    setSearch('')
    const pos = positions[b.booth]
    if (pos) {
      setActiveHall(pos.h as 'A' | 'B')
      const pad = 200 * scale
      setViewBox({
        x: pos.x * scale - pad * 2,
        y: pos.y * scale - pad * 2,
        w: pad * 6,
        h: pad * 5,
      })
    }
  }, [positions, scale])

  const handleSearchSelect = useCallback((b: BoothData) => {
    handleBoothClick(b)
  }, [handleBoothClick])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const factor = e.deltaY > 0 ? 1.15 : 0.87
    setViewBox(v => {
      const cx = v.x + v.w / 2
      const cy = v.y + v.h / 2
      const nw = v.w * factor
      const nh = v.h * factor
      return { x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh }
    })
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return
    isPanning.current = true
    panStart.current = { x: e.clientX, y: e.clientY, vx: viewBox.x, vy: viewBox.y }
  }, [viewBox])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const scaleX = viewBox.w / rect.width
    const scaleY = viewBox.h / rect.height
    const dx = (e.clientX - panStart.current.x) * scaleX
    const dy = (e.clientY - panStart.current.y) * scaleY
    setViewBox(v => ({ ...v, x: panStart.current.vx - dx, y: panStart.current.vy - dy }))
  }, [viewBox])

  const handleMouseUp = useCallback(() => {
    isPanning.current = false
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy)
    } else if (e.touches.length === 1) {
      isPanning.current = true
      panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, vx: viewBox.x, vy: viewBox.y }
    }
  }, [viewBox])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    if (e.touches.length === 2 && lastTouchDist.current !== null) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const dist = Math.sqrt(dx * dx + dy * dy)
      const factor = lastTouchDist.current / dist
      lastTouchDist.current = dist
      setViewBox(v => {
        const cx = v.x + v.w / 2
        const cy = v.y + v.h / 2
        const nw = v.w * factor
        const nh = v.h * factor
        return { x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh }
      })
    } else if (e.touches.length === 1 && isPanning.current && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect()
      const scaleX = viewBox.w / rect.width
      const scaleY = viewBox.h / rect.height
      const dx = (e.touches[0].clientX - panStart.current.x) * scaleX
      const dy = (e.touches[0].clientY - panStart.current.y) * scaleY
      setViewBox(v => ({ ...v, x: panStart.current.vx - dx, y: panStart.current.vy - dy }))
    }
  }, [viewBox])

  const handleTouchEnd = useCallback(() => {
    isPanning.current = false
    lastTouchDist.current = null
  }, [])

  const resetView = useCallback(() => {
    const b = hallBounds[activeHall]
    const pad = 20
    setViewBox({
      x: b.minX - pad,
      y: b.minY - pad,
      w: (b.maxX - b.minX) + pad * 2,
      h: (b.maxY - b.minY) + pad * 2,
    })
    setSelected(null)
    setPinned(null)
  }, [activeHall, hallBounds])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    )
  }

  const activeCats = [...new Set(booths.map(b => b.cat))].filter(c => c !== 'other').sort()

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold">Floor Plan</h1>
          <p className="text-gray-500 text-xs sm:text-sm">
            {hallBooths.length} exhibitors &middot; {isMobile ? 'Pinch to zoom, drag to pan' : 'Scroll to zoom, drag to pan'}
          </p>
        </div>
        <div className="flex gap-1 sm:gap-2">
          <button
            onClick={() => setActiveHall('A')}
            className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium ${activeHall === 'A' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            South ({booths.filter(b => positions[b.booth]?.h === 'A').length})
          </button>
          <button
            onClick={() => setActiveHall('B')}
            className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium ${activeHall === 'B' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            North ({booths.filter(b => positions[b.booth]?.h === 'B').length})
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-2">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search booth #, company, or industry..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm"
        />
        {search && (
          <button onClick={() => { setSearch(''); searchInputRef.current?.focus() }}
            className="absolute right-3 top-2 sm:top-2.5 text-gray-400 hover:text-gray-600 text-lg">&times;</button>
        )}
        {searchResults && searchResults.length > 0 && (
          <div className="absolute z-20 top-full mt-1 w-full bg-white rounded-xl shadow-xl border max-h-64 sm:max-h-80 overflow-y-auto">
            {searchResults.map(b => (
              <button key={b.id} onClick={() => handleSearchSelect(b)}
                className="w-full text-left px-3 sm:px-4 py-2 sm:py-2.5 hover:bg-indigo-50 flex items-center gap-2 sm:gap-3 border-b border-gray-50 last:border-0">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CAT_COLORS[b.cat] || '#94a3b8' }} />
                <span className="font-mono text-xs text-gray-500 w-10 sm:w-14">{b.booth}</span>
                <span className="text-sm text-gray-800 flex-1 truncate">{b.name}</span>
                <span className="text-xs text-gray-400 hidden sm:inline">{positions[b.booth]?.h === 'A' ? 'South' : 'North'}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Category filter — horizontal scroll on mobile */}
      <div className="flex gap-1 mb-2 overflow-x-auto pb-1 -mx-2 px-2 sm:flex-wrap sm:overflow-visible sm:mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
        <button onClick={() => setHighlightCat(null)}
          className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${!highlightCat ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}>
          All
        </button>
        {activeCats.map(cat => (
          <button key={cat} onClick={() => setHighlightCat(highlightCat === cat ? null : cat)}
            className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 whitespace-nowrap flex-shrink-0"
            style={{
              backgroundColor: highlightCat === cat ? CAT_COLORS[cat] : CAT_COLORS[cat] + '20',
              color: highlightCat === cat ? 'white' : CAT_COLORS[cat],
            }}>
            {CATEGORY_LABELS[cat as CategorySlug] || cat}
          </button>
        ))}
      </div>

      {/* Main layout: side-by-side desktop, stacked mobile */}
      <div className={isMobile ? 'flex flex-col' : 'flex gap-4'}>
        {/* SVG Floor Plan */}
        <div className={`${isMobile ? 'w-full' : 'flex-1 min-w-0'} relative`}>
          <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
            <button onClick={resetView} className="bg-white shadow rounded-lg px-2 py-1 text-xs text-gray-600 active:bg-gray-100">Reset</button>
            <button onClick={() => setViewBox(v => ({ x: v.x + v.w * 0.1, y: v.y + v.h * 0.1, w: v.w * 0.8, h: v.h * 0.8 }))}
              className="bg-white shadow rounded-lg w-8 h-8 flex items-center justify-center text-lg text-gray-600 active:bg-gray-100">+</button>
            <button onClick={() => setViewBox(v => ({ x: v.x - v.w * 0.1, y: v.y - v.h * 0.1, w: v.w * 1.2, h: v.h * 1.2 }))}
              className="bg-white shadow rounded-lg w-8 h-8 flex items-center justify-center text-lg text-gray-600 active:bg-gray-100">-</button>
          </div>

          <svg
            ref={svgRef}
            viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
            className="w-full bg-gray-50 rounded-xl border touch-none"
            style={{ height: isMobile ? '50vh' : 'calc(100vh - 240px)', cursor: isPanning.current ? 'grabbing' : 'grab' }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <defs>
              <filter id="pin-glow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            {hallBooths.map(b => {
              const pos = positions[b.booth]
              if (!pos || !pos.p) return null
              const color = CAT_COLORS[b.cat] || '#94a3b8'
              const isSelected = selected?.id === b.id
              const isPinnedBooth = !selected && pinned?.id === b.id
              const isNeighbor = selected && neighbors.some(n => n.id === b.id)
              const dimmed = highlightCat && b.cat !== highlightCat
              const points = parsePoints(pos.x, pos.y, pos.p, scale)
              const cx = pos.x * scale
              const cy = pos.y * scale
              const fontSize = Math.min(viewBox.w / 120, 3)

              return (
                <g key={b.id} onClick={(e) => { e.stopPropagation(); handleBoothClick(b) }} style={{ cursor: 'pointer' }}>
                  <polygon
                    points={points}
                    fill={dimmed ? '#e5e7eb' : color}
                    fillOpacity={dimmed ? 0.2 : (isSelected || isPinnedBooth) ? 1 : 0.7}
                    stroke={isSelected ? '#1e1b4b' : isPinnedBooth ? '#f59e0b' : isNeighbor ? '#4f46e5' : '#fff'}
                    strokeWidth={(isSelected || isPinnedBooth) ? 0.8 : isNeighbor ? 0.5 : 0.15}
                  />
                  {viewBox.w < 500 && !dimmed && (
                    <text x={cx} y={cy - 1} fontSize={fontSize} fill="#374151" textAnchor="middle" dominantBaseline="auto" style={{ pointerEvents: 'none' }}>
                      {b.booth}
                    </text>
                  )}
                </g>
              )
            })}
            {/* Pinned booth marker — always visible after closing panel */}
            {pinned && positions[pinned.booth]?.h === activeHall && (() => {
              const pos = positions[pinned.booth]
              const cx = pos.x * scale
              const cy = pos.y * scale
              const r = Math.max(viewBox.w / 80, 2)
              const labelSize = Math.max(viewBox.w / 100, 2.5)
              return (
                <g style={{ pointerEvents: 'none' }} filter="url(#pin-glow)">
                  <circle cx={cx} cy={cy} r={r * 2.5} fill="#f59e0b" fillOpacity={0.15}>
                    <animate attributeName="r" values={`${r * 2};${r * 3.5};${r * 2}`} dur="2s" repeatCount="indefinite" />
                    <animate attributeName="fill-opacity" values="0.2;0.05;0.2" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={cx} cy={cy} r={r} fill="#f59e0b" stroke="#fff" strokeWidth={r * 0.3} />
                  <rect
                    x={cx - labelSize * 4}
                    y={cy - r - labelSize * 2.5}
                    width={labelSize * 8}
                    height={labelSize * 1.8}
                    rx={labelSize * 0.4}
                    fill="#1e1b4b"
                    fillOpacity={0.9}
                  />
                  <text
                    x={cx}
                    y={cy - r - labelSize * 1.3}
                    fontSize={labelSize}
                    fill="#fff"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontWeight="bold"
                  >
                    {pinned.name.length > 18 ? pinned.name.substring(0, 16) + '...' : pinned.name}
                  </text>
                  <text
                    x={cx}
                    y={cy + r + labelSize * 1.2}
                    fontSize={labelSize * 0.7}
                    fill="#f59e0b"
                    textAnchor="middle"
                    fontWeight="bold"
                    fontFamily="monospace"
                  >
                    #{pinned.booth}
                  </text>
                </g>
              )
            })()}
          </svg>
        </div>

        {/* Pinned booth chip — shown when panel is closed */}
        {!selected && pinned && (
          <div className={`${isMobile ? 'fixed bottom-4 left-2 right-2 z-20' : 'absolute bottom-3 left-3 z-10'}`}>
            <button
              onClick={() => setSelected(pinned)}
              className="flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-xl shadow-lg text-sm w-full sm:w-auto"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 flex-shrink-0 animate-pulse" />
              <span className="font-mono text-xs text-amber-300">#{pinned.booth}</span>
              <span className="truncate flex-1 text-left">{pinned.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">Tap to reopen</span>
              <span onClick={(e) => { e.stopPropagation(); setPinned(null) }}
                className="text-gray-500 hover:text-white ml-1 flex-shrink-0">&times;</span>
            </button>
          </div>
        )}

        {/* Detail panel */}
        {selected && (
          isMobile ? (
            /* Mobile: bottom sheet */
            <div className="fixed inset-x-0 bottom-0 z-30 bg-white rounded-t-2xl shadow-2xl border-t max-h-[55vh] overflow-y-auto" style={{ animation: 'slide-up 0.3s ease-out' }}>
              {/* Drag handle */}
              <div className="flex justify-center pt-2 pb-1">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>

              <div className="sticky top-0 bg-white px-4 pb-2 border-b flex items-center justify-between z-10">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: CAT_COLORS[selected.cat] || '#94a3b8' }} />
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-bold" style={{ color: CAT_COLORS[selected.cat] }}>
                      Booth {selected.booth}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {CATEGORY_LABELS[selected.cat as CategorySlug] || selected.cat}
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 text-lg flex-shrink-0">&times;</button>
              </div>

              <div className="px-4 py-3">
                <Link to={`/companies/${selected.id}`} className="text-base font-semibold text-gray-900 hover:text-indigo-600 block mb-2">
                  {selected.name}
                </Link>

                {/* Quick facts — compact row for mobile */}
                {(selected.founded || selected.employees || selected.hq) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-3 bg-gray-50 rounded-lg px-3 py-2">
                    {selected.founded && (
                      <div><span className="text-gray-400">Est. </span><span className="font-semibold text-gray-700">{selected.founded}</span></div>
                    )}
                    {selected.employees && (
                      <div><span className="text-gray-400">Size </span><span className="font-semibold text-gray-700">{selected.employees}</span></div>
                    )}
                    {selected.hq && (
                      <div><span className="text-gray-400">HQ </span><span className="font-semibold text-gray-700">{selected.hq}</span></div>
                    )}
                    {selected.revenue && (
                      <div><span className="text-gray-400">Rev </span><span className="font-semibold text-gray-700">{selected.revenue}</span></div>
                    )}
                  </div>
                )}

                {selected.industry && <p className="text-xs text-gray-500 mb-2">{selected.industry}</p>}
                {selected.desc && <p className="text-sm text-gray-600 mb-3">{selected.desc}</p>}

                {/* Nearest booths */}
                <div className="border-t pt-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Nearby ({neighbors.length})
                  </h4>
                  <div className="grid grid-cols-1 gap-0.5">
                    {neighbors.slice(0, 15).map(n => (
                      <button key={n.id} onClick={() => handleBoothClick(n)}
                        className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg active:bg-gray-100">
                        <span className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: CAT_COLORS[n.cat] || '#94a3b8' }} />
                        <span className="font-mono text-xs text-gray-500 w-10">{n.booth}</span>
                        <span className="text-sm text-gray-700 flex-1 truncate">{n.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Desktop: side panel */
            <div className="w-80 flex-shrink-0 sticky top-20 self-start">
              <div className="card border-2" style={{ borderColor: CAT_COLORS[selected.cat] || '#94a3b8' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-lg font-bold" style={{ color: CAT_COLORS[selected.cat] }}>
                    Booth {selected.booth}
                  </span>
                  <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
                </div>
                <Link to={`/companies/${selected.id}`} className="text-lg font-semibold text-gray-900 hover:text-indigo-600 block mb-1">
                  {selected.name}
                </Link>
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-2"
                  style={{ backgroundColor: CAT_COLORS[selected.cat] + '20', color: CAT_COLORS[selected.cat] }}>
                  {CATEGORY_LABELS[selected.cat as CategorySlug] || selected.cat}
                </span>

                {/* Quick Facts grid */}
                {(selected.founded || selected.employees || selected.hq) && (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs mb-3 bg-gray-50 rounded-lg p-2.5">
                    {selected.founded && (
                      <div>
                        <div className="text-gray-400 font-medium">Founded</div>
                        <div className="text-gray-700 font-semibold">{selected.founded}</div>
                      </div>
                    )}
                    {selected.employees && (
                      <div>
                        <div className="text-gray-400 font-medium">Employees</div>
                        <div className="text-gray-700 font-semibold">{selected.employees}</div>
                      </div>
                    )}
                    {selected.hq && (
                      <div className="col-span-2">
                        <div className="text-gray-400 font-medium">Headquarters</div>
                        <div className="text-gray-700 font-semibold">{selected.hq}</div>
                      </div>
                    )}
                    {selected.revenue && (
                      <div className="col-span-2">
                        <div className="text-gray-400 font-medium">Revenue</div>
                        <div className="text-gray-700 font-semibold">{selected.revenue}</div>
                      </div>
                    )}
                  </div>
                )}

                {selected.industry && <p className="text-xs text-gray-500 mb-2">{selected.industry}</p>}
                {selected.desc && <p className="text-sm text-gray-600 mb-3">{selected.desc}</p>}

                <div className="border-t pt-3 mt-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Nearest Booths ({neighbors.length})
                  </h4>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {neighbors.map(n => (
                      <button key={n.id} onClick={() => handleBoothClick(n)}
                        className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: CAT_COLORS[n.cat] || '#94a3b8' }} />
                        <span className="font-mono text-xs text-gray-500 w-12">{n.booth}</span>
                        <span className="text-sm text-gray-700 flex-1 truncate">{n.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}
